import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppSettingsService, LoggerService, hd } from '@nx-fe/services';
import { Observable } from 'rxjs';
import { Ticket } from 'sspapp/modules/ticket/ticket.model';

const LOCALSTORAGE_AUTHDETAILS = 'ls.authorizationData';

@Injectable()
export class SspRestApiService {
  constructor(
    private appSettingsService: AppSettingsService,
    private httpUtil: HttpClient,
    @Inject('LOCALSTORAGE') private localStorage: Storage,
    private $log: LoggerService
  ) {
    this.xsightDeviceServiceUrl = this.appSettingsService.deviceServiceUrl;
  }

  private logPrefix = 'RestAPIProvider: authorization: ';
  private xsightDeviceServiceUrl: string;

  private getClaims = (): hd.ISotiAssistClaims => {
    const claims: hd.ISotiAssistClaims = JSON.parse(this.localStorage.getItem(LOCALSTORAGE_AUTHDETAILS)).claims;
    return claims;
  };

  public lockDevice(deviceId: string, phone: string, message: string, password: string) {
    this.$log.debug(this.logPrefix + 'lockDevice(): starting HTTP call ...');

    const actionInfo = {
      Action: 'Lock',
      Phone: phone ? phone : '',
      Message: message ? message : '',
      // AutoGenerateNewPasscode: password ? true : false,
      // Pin: password ? password : '',
      QueueForOfflineDelivery: true,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public wipeDevice(deviceId: string, password: string) {
    this.$log.debug(this.logPrefix + 'wipeDevice(): starting HTTP call ...');

    const actionInfo = {
      Action: 'Wipe',
      QueueForOfflineDelivery: true,
      ContractValue: {
        WipeActionGroup: {
          RevokeCertificates: false,
          BypassFactoryResetProtection: false,
          WipeSdCard: false,
          RemoteWipeType: 'DoWipeProtected',
          PreserveDataPlan: false,
          DisallowProximitySetup: false,
          Pin: '',
          RequestRequiresNetworkTether: false,
          ObliterationBehavior: 'DoNotObliterate',
          SendBy: '',
          SendByDescriptionMobiControl: '',
          SendByDescriptionPlatformNotificationService: '',
        },
      },
      FeedbackReason: null,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public unenrollDevice(deviceId: string) {
    this.$log.debug(this.logPrefix + 'unenrollDevice(): starting HTTP call ...');

    const actionInfo = {
      Action: 'Unenroll',
      QueueForOfflineDelivery: true,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public sendMessage(deviceId: string, message: string) {
    this.$log.debug(this.logPrefix + 'sendMessage(): starting HTTP call ...');

    const actionInfo = {
      Action: 'SendMessage',
      Message: message,
      QueueForOfflineDelivery: false,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public locateDevice(deviceId: string) {
    this.$log.debug(this.logPrefix + 'locateDevice(): starting HTTP call ...');

    const actionInfo = {
      Action: 'Locate',
      QueueForOfflineDelivery: true,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public setPassword(deviceId: string, password: string) {
    this.$log.debug(this.logPrefix + 'setPassword(): starting HTTP call ...');

    const actionInfo = {
      Action: 'ResetPasscode',
      Passcode: password,
      SendBy: 'MobiControl',
      QueueForOfflineDelivery: true,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public removePassword(deviceId: string) {
    this.$log.debug(this.logPrefix + 'removePassword(): starting HTTP call ...');

    const actionInfo = {
      Action: 'DisablePasscodeLock',
      SendBy: 'MobiControl',
      QueueForOfflineDelivery: true,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public checkInDevice(deviceId: string) {
    this.$log.debug(this.logPrefix + 'checkInDevice(): starting HTTP call ...');

    const actionInfo = {
      Action: 'CheckIn',
      QueueForOfflineDelivery: true,
    };

    const deviceActionInfo = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.callDeviceRestService(deviceActionInfo);
  }

  public getDeviceList() {
    console.log(this.logPrefix + 'getDeviceList(): starting HTTP call ...');
    return this.httpUtil.get<any>('/sotixsight/device/userName?userName=' + this.getClaims().UserName);
  }

  public getLastDeviceLocation(deviceId) {
    this.$log.debug(this.logPrefix + 'getLastDeviceLocation(): starting HTTP call ...');
    return this.httpUtil.get<any>('/sotixsight/device/lastKnownLocation?deviceId=' + deviceId);
  }

  private callDeviceRestService(data) {
    return this.httpUtil.post(this.xsightDeviceServiceUrl + 'deviceAction', data);
  }

  public getTicketData(filter: string, skip: number, take: number): Observable<any> {
    const data = {
      filterParams: JSON.stringify({ filter }, null, 0),
      take,
      skip,
    };

    return this.httpUtil.post<any>(this.appSettingsService.ticketServiceUrl + 'ssp/data', data);
  }

  public getTicket(ticketId: string): Observable<Ticket> {
    return this.httpUtil.get<Ticket>(`/sotixsight/tickets/${ticketId}`);
  }
}
